"use client"

import { Suspense } from "react"
import Link from "next/link"
import { m } from "framer-motion"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { ArrowRight, Sparkles } from "lucide-react"
import TextReveal from "./animated/text-reveal"
import GradientBackground from "./animated/gradient-background"
import HeroVideo from "./video/hero-video"

export default function HeroWithVideo() {
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      <GradientBackground />

      {/* Video Background with Loader */}
      <div className="absolute inset-0 opacity-100">
        <Suspense fallback={<div className="w-full h-full flex items-center justify-center"><div className="rmm-loader" aria-label="Loading video" /></div>}>
          <HeroVideo />
        </Suspense>
      </div>

      <div className="relative z-10 container mx-auto px-4 text-center">
        {/* Badge with keyword */}
        <m.div initial={{ opacity: 0, y: 50 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1 }}>
          <Badge className="bg-white/10 text-white border-white/20 mb-6 backdrop-blur-sm">
            <Sparkles className="h-4 w-4 mr-2" />
            Metal Products Colombo & Industrial Hardware Sri Lanka
          </Badge>
        </m.div>

        <TextReveal delay={0.2}>
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight">
            {/* <span className="bg-gradient-to-r from-white via-slate-200 to-white bg-clip-text text-transparent">
              Modern Metal Warehouse & Steel Suppliers
            </span> */}
            {/* <br /> */}
            <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent">
              Quality Industrial Hardware Solutions in Sri Lanka
            </span>
          </h1>
        </TextReveal>

        <TextReveal delay={0.4}>
          <p className="text-xl md:text-2xl text-slate-300 mb-8 max-w-4xl mx-auto leading-relaxed">
            Real Metro Metal is your trusted partner for <strong>metal products in Colombo</strong> and <strong>industrial hardware in Sri Lanka</strong>. Experience quality, reliability, and competitive prices with our comprehensive range of products.
          </p>
        </TextReveal>

        <m.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="flex flex-col sm:flex-row gap-4 justify-center"
        >
          <m.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <Button
              size="lg"
              className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-2xl px-8 py-4 text-lg"
              asChild
            >
              <Link href="/products">
                Browse Products
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
          </m.div>
          <m.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <Button
              size="lg"
              variant="outline"
              className="border-white/30 text-white hover:bg-white hover:text-slate-900 bg-white/10 backdrop-blur-sm px-8 py-4 text-lg"
              asChild
            >
              <Link href="/contact">Get a Quote</Link>
            </Button>
          </m.div>
        </m.div>
      </div>

      {/* Scroll indicator */}
      <m.div
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 2, repeat: Number.POSITIVE_INFINITY }}
      >
        <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center" aria-label="Scroll down">
          <div className="w-1 h-3 bg-white/50 rounded-full mt-2" />
        </div>
      </m.div>
    </section>
  )
}
